import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { BaseCard, BaseCardBody, BaseCardHeader } from '@unite-us/ui';
import { browserHistory } from 'common/utils/browserHistory';
import { cl2372SocialCarePlanTab } from 'common/utils/FeatureFlags/flags';
import FacesheetCases from '../cases/FacesheetCases';
import FacesheetReferrals from '../referrals/FacesheetReferrals';
import CareTeamSection from './CareTeamSection';
import EligibilityAssessmentSection from './EligibilityAssessmentSection';
import FamilyMembersSection from './FamilyMembersSection';
import HRSNServiceConsiderationsSection from './HRSNServiceConsiderationsSection';
import MemberContactSection from './MemberContactSection';
import MessagesSection from './MessagesSection';
import NotesSection from './NotesSection';
import ScreeningHistorySection from './ScreeningHistorySection';
import { MEMBER_CONTACT_SECTION_TITLE } from './memberContact.constants';

export function FacesheetSocialCarePlan({
  contact,
  getClientRelationship,
  params,
  socialCarePlanTabEnabled,
  visibilityConfig,
}) {
  const contactId = params?.id;
  const isTabVisible =
    socialCarePlanTabEnabled &&
    visibilityConfig?.socialCarePlan?.showTab;
  const showCareCoordinator = visibilityConfig?.overview?.showCareCoordinator ?? true;
  const showCareTeam = visibilityConfig?.overview?.showCareTeam ?? true;
  const showFamilyMembers = visibilityConfig?.overview?.showFamilyMembers ?? true;

  const sectionDefinitions = [
    {
      title: MEMBER_CONTACT_SECTION_TITLE,
      renderBody: (currentContact) => (
        <>
          <MemberContactSection contact={currentContact} />
          {(showCareCoordinator || showCareTeam) && (
            <CareTeamSection
              className={showFamilyMembers ? 'px-5 pt-6' : 'px-5 pt-6 pb-5'}
              contact={currentContact}
              getClientRelationship={getClientRelationship}
              sectionTitle="Care Team"
              visibilityConfig={visibilityConfig}
            />
          )}
          {currentContact && showFamilyMembers && (
            <FamilyMembersSection
              className="px-5 pt-6 pb-5"
              contact={currentContact}
              sectionTitle="Family Members"
            />
          )}
        </>
      ),
    },
    {
      title: 'Screening and Navigation History',
      renderBody: () => (
        <>
          <ScreeningHistorySection
            className="px-5 py-5"
            embeddedMode
            params={params}
          />
          <EligibilityAssessmentSection
            className="px-5 pt-6"
            embeddedMode
            params={params}
            sectionTitle="Eligibility Assessments"
          />
          {/*
            Re-add Major Life Events here once the section has a backed read/write flow.
            <MajorLifeEventsSection className="px-5 pt-6 pb-5" />
          */}
        </>
      ),
    },
    {
      title: 'HRSN Service Considerations',
      renderBody: (currentContact) => <HRSNServiceConsiderationsSection contact={currentContact} />,
    },
    {
      title: 'Referrals',
      renderCard: () => <FacesheetReferrals params={params} />,
    },
    {
      title: 'Cases',
      renderCard: () => (
        <FacesheetCases
          params={params}
          route={{ path: '/facesheet/:id/social-care-plan' }}
        />
      ),
    },
    {
      title: 'Messages',
      renderCard: () => <MessagesSection contact={contact} />,
    },
    {
      title: 'Notes',
      renderCard: () => <NotesSection contact={contact} />,
    },
  ];

  useEffect(() => {
    if (!isTabVisible && contactId) {
      browserHistory.replace(`/facesheet/${contactId}`);
    }
  }, [contactId, isTabVisible]);

  if (!isTabVisible) {
    return null;
  }

  return (
    <div className="facesheet-social-care-plan">
      {sectionDefinitions.map(({ title, renderBody, renderCard }, index) => {
        const className = index < sectionDefinitions.length - 1 ? 'mb-6' : '';

        if (renderCard) {
          return (
            <div key={title} className={className}>
              {renderCard()}
            </div>
          );
        }

        return (
          <BaseCard key={title} className={className}>
            <BaseCardHeader
              title={(
                <span className="text-base font-semibold leading-6 text-brand-blue">
                  {title}
                </span>
              )}
            />
            <BaseCardBody>
              {renderBody ? renderBody(contact) : <div className="h-6" aria-hidden="true" />}
            </BaseCardBody>
          </BaseCard>
        );
      })}
    </div>
  );
}

FacesheetSocialCarePlan.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string,
  }),
  getClientRelationship: PropTypes.func,
  params: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  socialCarePlanTabEnabled: PropTypes.bool.isRequired,
  visibilityConfig: PropTypes.shape({
    overview: PropTypes.shape({
      showCareCoordinator: PropTypes.bool,
      showCareTeam: PropTypes.bool,
      showFamilyMembers: PropTypes.bool,
    }),
    socialCarePlan: PropTypes.shape({
      showTab: PropTypes.bool.isRequired,
    }),
  }).isRequired,
};

FacesheetSocialCarePlan.defaultProps = {
  contact: null,
  getClientRelationship: () => {},
};

const mapStateToProps = (state, ownProps) => ({
  contact: _.find(_.get(state, 'contacts.contacts', []), { id: ownProps.params.id }),
  socialCarePlanTabEnabled: cl2372SocialCarePlanTab(state),
});

export default connect(mapStateToProps)(FacesheetSocialCarePlan);
